/**
 * Sélecteur Juridiction + Langue.
 *
 * Deux réglages distincts :
 *  - Juridiction (fr / eu) : le droit consulté et la page d'accueil — drapeaux.
 *  - Langue (fr / en / de)      : la langue de l'interface — noms natifs, sans drapeau
 *    (une langue n'est pas un pays).
 *
 * Variante `popover` pour le header, `inline` pour le menu mobile.
 */
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  getCurrentLanguage
} from '@/i18n';
import {
  MARKETS,
  MARKET_HOME,
  type Market,
  getMarket,
  setMarket,
  usePreferredMarket
} from '@/lib/market';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const MARKET_FLAGS: Record<Market, string> = { fr: '🇫🇷', eu: '🇪🇺' };

const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  fr: 'Français',
  en: 'English',
  de: 'Deutsch'
};

interface LocaleSwitcherProps {
  variant?: 'popover' | 'inline';
  className?: string;
  /** Appelé après un choix (ex : fermer le menu mobile). */
  onChange?: () => void;
}

function Choices({ onChange }: { onChange?: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  usePreferredMarket(); // re-render sur changement de juridiction
  const market = getMarket();
  const language = getCurrentLanguage();

  const chooseMarket = (m: Market) => {
    setMarket(m);
    if (m !== market) navigate(MARKET_HOME[m]);
    onChange?.();
  };

  const chooseLanguage = (lng: SupportedLanguage) => {
    i18n.changeLanguage(lng);
    onChange?.();
  };

  const option = (active: boolean) =>
    `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
      active
        ? 'bg-violet-600/10 text-violet-700 dark:text-violet-300 font-semibold'
        : 'hover:bg-accent'
    }`;

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-labelledby="locale-jurisdiction">
        <p
          id="locale-jurisdiction"
          className="px-3 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider"
        >
          {t('locale.jurisdiction')}
        </p>
        {MARKETS.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={market === m}
            onClick={() => chooseMarket(m)}
            className={option(market === m)}
          >
            <span aria-hidden="true" className="text-base leading-none">
              {MARKET_FLAGS[m]}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block">{t(`locale.markets.${m}.name`)}</span>
              <span className="block text-[11px] font-normal text-muted-foreground truncate">
                {t(`locale.markets.${m}.coverage`)}
              </span>
            </span>
            {market === m && <CheckIcon />}
          </button>
        ))}
      </div>

      <div
        role="radiogroup"
        aria-labelledby="locale-language"
        className="border-t pt-3"
      >
        <p
          id="locale-language"
          className="px-3 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider"
        >
          {t('common.language')}
        </p>
        {SUPPORTED_LANGUAGES.map((lng) => (
          <button
            key={lng}
            type="button"
            role="radio"
            aria-checked={language === lng}
            lang={lng}
            onClick={() => chooseLanguage(lng)}
            className={option(language === lng)}
          >
            <span className="w-6 text-[11px] font-bold text-muted-foreground">
              {lng.toUpperCase()}
            </span>
            <span className="flex-1">{LANGUAGE_NAMES[lng]}</span>
            {language === lng && <CheckIcon />}
          </button>
        ))}
      </div>
    </div>
  );
}

const CheckIcon = () => (
  <svg
    className="w-4 h-4 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

export function LocaleSwitcher({
  variant = 'popover',
  className = '',
  onChange
}: LocaleSwitcherProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  usePreferredMarket();
  void i18n.language;
  const market = getMarket();
  const language = getCurrentLanguage();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (variant === 'inline') {
    return (
      <div className={className}>
        <Choices onChange={onChange} />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('locale.button_label', {
          jurisdiction: t(`locale.markets.${market}.name`),
          language: LANGUAGE_NAMES[language]
        })}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-transparent hover:border-border/50 hover:bg-accent text-xs font-semibold transition-colors"
      >
        <span aria-hidden="true" className="text-sm leading-none">
          {MARKET_FLAGS[market]}
        </span>
        <span>{t(`locale.markets.${market}.short`)}</span>
        <span className="text-muted-foreground/50">·</span>
        <span className="text-muted-foreground">{language.toUpperCase()}</span>
        <svg
          className={`w-3 h-3 text-muted-foreground transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('locale.title')}
          className="absolute right-0 top-full mt-2 w-72 rounded-xl border bg-card shadow-lg p-2 z-50"
        >
          <Choices onChange={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

export default LocaleSwitcher;
